"use client"

import type React from "react"

import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Textarea } from "../ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs"
import { Upload, Camera, User, Lock, Loader2, X, Share2 } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { authAPI, API_BASE_URL } from "../../services/api"

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  bio: string;
  photo?: File | null;
  // Social media fields for formateurs
  facebook?: string;
  instagram?: string;
  linkedin?: string;
}

interface PasswordFormData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function EditProfilePage() {
  const { user, checkAuthStatus } = useAuth();

  // Profile form state
  const [profileData, setProfileData] = useState<ProfileFormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    bio: "",
    photo: null,
    facebook: "",
    instagram: "",
    linkedin: "",
  });

  // Password form state
  const [passwordData, setPasswordData] = useState<PasswordFormData>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  // Photo preview state
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Loading and error states
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phone: user.phone || "",
        address: user.address || "",
        bio: user.bio || "",
        photo: null,
        facebook: user.socialMedia?.facebook || "",
        instagram: user.socialMedia?.instagram || "",
        linkedin: user.socialMedia?.linkedin || "",
      });
    }
  }, [user]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (profileSuccess) {
      const timer = setTimeout(() => setProfileSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [profileSuccess]);

  useEffect(() => {
    if (passwordSuccess) {
      const timer = setTimeout(() => setPasswordSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [passwordSuccess]);

  // Generate avatar with first letter of first name
  const generateAvatarWithInitials = (firstName: string) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 128;
    canvas.height = 128;

    if (ctx) {
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, 128, 128);
      gradient.addColorStop(0, '#667eea');
      gradient.addColorStop(1, '#764ba2');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 128, 128);

      // Text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(firstName.charAt(0).toUpperCase(), 64, 64);
    }

    return canvas.toDataURL();
  };

  // Get current photo to display
  const getCurrentPhoto = () => {
    if (photoPreview) return photoPreview;
    if (user?.photo) {
      // Backend uploads served at /uploads endpoint
      return `${API_BASE_URL}${user.photo}`;
    }
    if (profileData.firstName) {
      return generateAvatarWithInitials(profileData.firstName);
    }
    return "/placeholder.svg";
  };

  // Handle photo selection
  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setProfileError('Veuillez sélectionner un fichier image valide');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setProfileError('La taille de l\'image ne doit pas dépasser 5MB');
        return;
      }

      // Update profile data with the file
      setProfileData(prev => ({ ...prev, photo: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Clear any existing errors
      setProfileError(null);
    }
  };

  // Remove selected photo
  const handlePhotoRemove = () => {
    setProfileData(prev => ({ ...prev, photo: null }));
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleProfileChange = (field: keyof ProfileFormData, value: string) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (profileError) setProfileError(null);
  };

  const handlePasswordChange = (field: keyof PasswordFormData, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (passwordError) setPasswordError(null);
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProfileLoading(true);
    setProfileError(null);
    setProfileSuccess(null);

    try {
      // Create FormData to handle file upload
      const formData = new FormData();

      // Add all profile fields to FormData
      formData.append('firstName', profileData.firstName);
      formData.append('lastName', profileData.lastName);
      formData.append('email', profileData.email);
      formData.append('phone', profileData.phone || '');
      formData.append('address', profileData.address || '');
      formData.append('bio', profileData.bio || '');

      // Add social media fields for formateurs
      if (user?.role === 'formateur') {
        formData.append('facebook', profileData.facebook || '');
        formData.append('instagram', profileData.instagram || '');
        formData.append('linkedin', profileData.linkedin || '');
      }

      // Add photo if selected
      if (profileData.photo) {
        formData.append('photo', profileData.photo);
      }

      const response = await authAPI.updateProfile(formData);

      if (response.data.success) {
        setProfileSuccess("Profil mis à jour avec succès !");
        // Clear photo selection states
        setProfileData(prev => ({ ...prev, photo: null }));
        setPhotoPreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh user data in context
        await checkAuthStatus();
      }
    } catch (error: any) {
      const message = error.response?.data?.error || "Erreur lors de la mise à jour du profil";
      setProfileError(message);
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsPasswordLoading(true);
    setPasswordError(null);
    setPasswordSuccess(null);

    // Validate password confirmation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Les mots de passe ne correspondent pas");
      setIsPasswordLoading(false);
      return;
    }

    // Validate password strength
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
    if (!passwordRegex.test(passwordData.newPassword)) {
      setPasswordError("Le mot de passe doit contenir au moins 8 caractères, une lettre majuscule, une lettre minuscule et un chiffre");
      setIsPasswordLoading(false);
      return;
    }

    try {
      const response = await authAPI.changePassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });

      if (response.data.success) {
        setPasswordSuccess("Mot de passe mis à jour avec succès !");
        // Clear password form
        setPasswordData({
          oldPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      }
    } catch (error: any) {
      const message = error.response?.data?.error || "Erreur lors de la mise à jour du mot de passe";
      setPasswordError(message);
    } finally {
      setIsPasswordLoading(false);
    }
  };

  // Loading state while user data is being fetched
  if (!user) {
    return (
      <div className="container px-4 py-8 md:px-6 md:py-12">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="container px-4 py-8 md:px-6 md:py-12">
      <div className="flex flex-col gap-8">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Modifier mon profil</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Mettez à jour vos informations personnelles et votre mot de passe
          </p>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:w-auto">
            <TabsTrigger value="profile">
              <User className="mr-2 h-4 w-4" />
              Informations
            </TabsTrigger>
            <TabsTrigger value="password">
              <Lock className="mr-2 h-4 w-4" />
              Mot de passe
            </TabsTrigger>
          </TabsList>

          {/* Onglet Informations */}
          <TabsContent value="profile" className="mt-6">
            <Card>
              <form onSubmit={handleProfileSubmit}>
                <CardHeader>
                  <CardTitle>Informations personnelles</CardTitle>
                  <CardDescription>Mettez à jour vos informations de profil</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Success/Error Messages */}
                  {profileSuccess && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-800 text-sm">
                      {profileSuccess}
                    </div>
                  )}
                  {profileError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                      {profileError}
                    </div>
                  )}

                  {/* Photo Upload Section */}
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative w-32 h-32 group">
                      <img
                        src={getCurrentPhoto()}
                        alt={`${profileData.firstName} ${profileData.lastName}`}
                        className="rounded-full object-cover border-4 border-white shadow-md w-full h-full absolute inset-0"
                      />

                      {/* Photo Upload Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                           onClick={() => fileInputRef.current?.click()}>
                        <Camera className="h-8 w-8 text-white" />
                      </div>

                      {/* Remove Photo Button */}
                      {(profileData.photo || photoPreview) && (
                        <button
                          type="button"
                          onClick={handlePhotoRemove}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>

                    {/* Upload Controls */}
                    <div className="flex flex-col items-center gap-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoSelect}
                        className="hidden"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Changer la photo
                      </Button>

                      {profileData.photo && (
                        <span className="text-sm text-gray-500 text-center">
                          Photo sélectionnée: {profileData.photo.name}
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      Formats acceptés: JPG, PNG, GIF • Taille max: 5MB
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">Prénom</Label>
                      <Input
                        id="firstName"
                        value={profileData.firstName}
                        onChange={(e) => handleProfileChange('firstName', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Nom de famille</Label>
                      <Input
                        id="lastName"
                        value={profileData.lastName}
                        onChange={(e) => handleProfileChange('lastName', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileData.email}
                        onChange={(e) => handleProfileChange('email', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        value={profileData.phone}
                        onChange={(e) => handleProfileChange('phone', e.target.value)}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="address">Adresse</Label>
                      <Input
                        id="address"
                        value={profileData.address}
                        onChange={(e) => handleProfileChange('address', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bio">Biographie</Label>
                    <Textarea
                      id="bio"
                      rows={4}
                      value={profileData.bio}
                      onChange={(e) => handleProfileChange('bio', e.target.value)}
                      placeholder="Parlez-nous de vous..."
                    />
                  </div>

                  {/* Social Media Section - Only for Formateurs */}
                  {user?.role === 'formateur' && (
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pt-4 border-t">
                        <Share2 className="h-5 w-5 text-gray-600" />
                        <h3 className="text-lg font-medium">Réseaux sociaux</h3>
                      </div>
                      <p className="text-sm text-gray-500">
                        Ajoutez vos profils de réseaux sociaux pour permettre aux étudiants de vous suivre
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="facebook">Facebook</Label>
                          <Input
                            id="facebook"
                            value={profileData.facebook || ''}
                            onChange={(e) => handleProfileChange('facebook', e.target.value)}
                            placeholder="https://facebook.com/votre-profil"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="instagram">Instagram</Label>
                          <Input
                            id="instagram"
                            value={profileData.instagram || ''}
                            onChange={(e) => handleProfileChange('instagram', e.target.value)}
                            placeholder="https://instagram.com/votre-profil"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="linkedin">LinkedIn</Label>
                          <Input
                            id="linkedin"
                            value={profileData.linkedin || ''}
                            onChange={(e) => handleProfileChange('linkedin', e.target.value)}
                            placeholder="https://linkedin.com/in/votre-profil"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button variant="outline" type="button" asChild>
                    <a href="/dashboard">Annuler</a>
                  </Button>
                  <Button type="submit" disabled={isProfileLoading}>
                    {isProfileLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Enregistrer les modifications
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>

          {/* Onglet Mot de passe */}
          <TabsContent value="password" className="mt-6">
            <Card>
              <form onSubmit={handlePasswordSubmit}>
                <CardHeader>
                  <CardTitle>Changer le mot de passe</CardTitle>
                  <CardDescription>Mettez à jour votre mot de passe pour sécuriser votre compte</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Success/Error Messages */}
                  {passwordSuccess && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-800 text-sm">
                      {passwordSuccess}
                    </div>
                  )}
                  {passwordError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-800 text-sm">
                      {passwordError}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="current-password">Mot de passe actuel</Label>
                    <Input
                      id="current-password"
                      type="password"
                      value={passwordData.oldPassword}
                      onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password">Nouveau mot de passe</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                      required
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Le mot de passe doit contenir au moins 8 caractères, une lettre majuscule, une lettre minuscule et
                      un chiffre.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password">Confirmer le nouveau mot de passe</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                      required
                    />
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between">
                  <Button variant="outline" type="button" asChild>
                    <a href="/dashboard">Annuler</a>
                  </Button>
                  <Button type="submit" disabled={isPasswordLoading}>
                    {isPasswordLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Mettre à jour le mot de passe
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
